import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { ContentListDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveColorName,
  resolveElasticContentCategoryValues,
  resolveSpacingSize,
  resolveTheme,
} from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import CMSWrapper from '../shared/CMSWrapper';
import useElasticRelated from '../shared/Hooks/elasticSearch/useElasticRelated';
import FeaturedContent from './FeaturedContent';

const MappedFeaturedContent = ({
  presentation,
  curatedList,
  contentButtons,
  pageModels,
  page,
}: any) => {
  const { esLabels, taxonomy: taxonomies = {} } = pageModels;
  const {
    topSpacing,
    bottomSpacing,
    containerTopBackgroundColor,
    containerBottomBackgroundColor,
    template,
    cardColor,
    isCardImages,
    contentCategoryRelated01,
    contentCategoryRelated02,
    contentCategoryRelated03,
    contentCategoryRelated04,
    contentCategoryRelated05,
  } = presentation;

  const contentCategoriesRelated = [
    contentCategoryRelated01,
    contentCategoryRelated02,
    contentCategoryRelated03,
    contentCategoryRelated04,
    contentCategoryRelated05,
  ]?.filter((contentCategoryRelated: string) => !!contentCategoryRelated);

  const contentCategoryValues = resolveElasticContentCategoryValues({
    contentCategoryKeys: contentCategoriesRelated,
    taxonomies,
  });

  const containerProps = useMemo(
    () => ({
      topSpacing,
      bottomSpacing,
      topBackgroundColor: containerTopBackgroundColor || containerBottomBackgroundColor,
      bottomBackgroundColor: containerBottomBackgroundColor,
      cardVerticalAlignmentNoImage: 'flex-start',
      contentCategoriesRelated,
    }),
    [
      bottomSpacing,
      containerBottomBackgroundColor,
      containerTopBackgroundColor,
      topSpacing,
      contentCategoriesRelated,
    ],
  );
  const theme = useMemo(
    () =>
      resolveTheme('bg-color')({
        rawVal: containerTopBackgroundColor || containerBottomBackgroundColor,
      }),
    [containerBottomBackgroundColor, containerTopBackgroundColor],
  );

  const buttonLink = {
    buttonStyle: 'primary',
    displayText: esLabels?.read_on || 'Read on',
  };

  const titleExclusion = React.useMemo(() => {
    return curatedList?.contentList?.map((record: any) => record?.cardHeading) || [];
  }, [curatedList?.contentList]);

  const { es, curatedRelatedContent, search, sortOptions, status, allLabels, engine, searchKey } =
    useElasticRelated({
      relatedContentList: curatedList?.contentList,
      pageModels,
      page,
      isElasticAllowed: !!contentCategoryValues?.length,
      contentCategoryFallback: contentCategoryValues,
      sortBy: 'most-recent',
      buttonLink,
      excludedFilters: [{ title: titleExclusion }],
    });

  const componentProps = React.useMemo(
    () => ({
      curatedList: { ...curatedList, contentList: curatedRelatedContent },
      theme,
      template,
      contentButtons,
      cardColor,
      isCardImages,
      cardTheme: resolveTheme('bg-color')({ rawVal: cardColor }),
    }),
    [cardColor, contentButtons, curatedList, curatedRelatedContent, isCardImages, template, theme],
  );

  const contextValue = useMemo(
    () => ({
      queryParams: search,
      updateQueryParams: () => {},
      sortOptions,
      pagination: es.response.pagination,
      facets: es.facets,
      hideFilters: ['tax_content_category'],
      // eslint-disable-next-line no-nested-ternary
      status,
      results: es.response.results,
      labels: allLabels,
      manualUpdate: true,
      engine,
      requestID: es.response?.meta?.request_id,
      searchKey,
    }),
    [
      search,
      sortOptions,
      es.response.pagination,
      es.response.results,
      es.response?.meta?.request_id,
      es.facets,
      status,
      allLabels,
      engine,
      searchKey,
    ],
  );

  return (
    <div
      data-datalayer-position="featured_content"
      data-datalayer-variant={presentation?.datalayerVariant}
    >
      <ElasticComponentContext.Provider value={contextValue}>
        <FeaturedContent {...componentProps} presentation={containerProps} />
      </ElasticComponentContext.Provider>
    </div>
  );
};

const FeaturedContentCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    curatedDocument: ['curatedList', ContentListDocumentMapping],
    componentParameterMap: [
      'presentation',
      {
        template: 'template',
        'container-top-background-color': ['containerTopBackgroundColor', resolveColorName],
        'container-bottom-background-color': ['containerBottomBackgroundColor', resolveColorName],
        'top-spacing': ['topSpacing', resolveSpacingSize],
        'bottom-spacing': ['bottomSpacing', resolveSpacingSize],
        'secondary-card-color': ['cardColor', resolveColorName],
        'include-card-images': ['isCardImages', resolveBool],
        'variant-label': 'datalayerVariant',
        elastic_content_0: 'contentCategoryRelated01',
        elastic_content_1: 'contentCategoryRelated02',
        elastic_content_2: 'contentCategoryRelated03',
        elastic_content_3: 'contentCategoryRelated04',
        elastic_content_4: 'contentCategoryRelated05',
      },
    ],
  });

  const requiredFields = useRef({
    curatedDocument: '<b>Curated Content</b> required',
  });

  const contentButtonNames = useRef({
    curatedDocument: 'curatedList',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedFeaturedContent}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default FeaturedContentCMSWrapper;
